import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  privateConversations,
  privateMessagesDashboardStats,
  privateMessagesRules,
  blockedUsersList,
  conversationTypeLabels,
  conversationTypeIcons,
  attachmentTypeConfig,
  getUnreadCountTotal,
  getPinnedConversations,
  getOnlineContacts,
  type PrivateConversation,
  type ConversationType,
} from '@/mocks/rfwPrivateMessages';

function ConversationPreview({ conversation }: { conversation: PrivateConversation }) {
  const otherParticipant = conversation.participants[0];
  const typeKey = conversation.type;
  const typeInfo = { badge: '', ring: '', icon: '' };
  if (typeKey === 'oyente-oyente') { typeInfo.badge = 'bg-emerald-100 text-emerald-700 border-emerald-200'; typeInfo.ring = 'ring-emerald-400'; }
  else if (typeKey === 'oyente-locutor') { typeInfo.badge = 'bg-amber-100 text-amber-700 border-amber-200'; typeInfo.ring = 'ring-amber-400'; }
  else if (typeKey === 'oyente-moderador') { typeInfo.badge = 'bg-rose-100 text-rose-700 border-rose-200'; typeInfo.ring = 'ring-rose-400'; }
  else { typeInfo.badge = 'bg-violet-100 text-violet-700 border-violet-200'; typeInfo.ring = 'ring-violet-400'; }

  return (
    <Link
      to="/mensajes-privados"
      className="flex items-center gap-3 p-3.5 rounded-xl bg-background-50 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 group"
    >
      <div className="relative shrink-0">
        <img
          src={otherParticipant.photoUrl}
          alt={otherParticipant.name}
          className="w-11 h-11 rounded-full object-cover"
        />
        {otherParticipant.isOnline && (
          <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-background-50"></span>
        )}
        {otherParticipant.isTyping && (
          <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-background-50 flex items-center justify-center">
            <span className="flex gap-0.5">
              <span className="w-1 h-1 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-1 h-1 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-1 h-1 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </span>
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between mb-0.5">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{otherParticipant.name}</p>
            <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full border ${typeInfo.badge}`}>
              {conversationTypeLabels[typeKey].split(' ↔ ')[0]}
            </span>
          </div>
          <span className="text-[10px] text-foreground-400 shrink-0 ml-2">{conversation.lastMessageRelative}</span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-foreground-500 line-clamp-1 pr-2">{conversation.lastMessage}</p>
          {conversation.unreadCount > 0 && (
            <span className="shrink-0 w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
      {conversation.isPinned && (
        <i className="ri-pushpin-fill text-[10px] text-rose-400 shrink-0"></i>
      )}
    </Link>
  );
}

export default function MensajesPrivadosSection() {
  const [activeTab, setActiveTab] = useState<'conversaciones' | 'adjuntos' | 'bloqueados'>('conversaciones');
  const [typeFilter, setTypeFilter] = useState<ConversationType | 'todas'>('todas');

  const pinnedConvs = getPinnedConversations();
  const totalUnread = getUnreadCountTotal();
  const onlineContacts = getOnlineContacts();

  const filteredConvs = typeFilter === 'todas'
    ? privateConversations
    : privateConversations.filter((c) => c.type === typeFilter);

  const tabs = [
    { id: 'conversaciones' as const, label: 'Conversaciones', icon: 'ri-message-2-line', badge: totalUnread },
    { id: 'adjuntos' as const, label: 'Tipos de Adjunto', icon: 'ri-attachment-2' },
    { id: 'bloqueados' as const, label: 'Bloqueados', icon: 'ri-forbid-line', badge: blockedUsersList.length },
  ];

  const typeFilters: { id: ConversationType | 'todas'; label: string }[] = [
    { id: 'todas', label: 'Todas' },
    { id: 'oyente-oyente', label: 'Oyente↔Oyente' },
    { id: 'oyente-locutor', label: 'Oyente↔Locutor' },
    { id: 'oyente-moderador', label: 'Oyente↔Mod' },
    { id: 'locutor-locutor', label: 'Locutor↔Locutor' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            SISTEMA DE MENSAJES PRIVADOS
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Comunicate en <span className="text-rose-600">Privado</span> con la Comunidad
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Mensajes directos entre oyentes, locutores y moderadores. Enviá imágenes, audio, stickers y GIFs. Construí conexiones reales sin interferir con la magia de la Cabina.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Conversaciones Activas', value: (privateMessagesDashboardStats.totalConversations / 1000).toFixed(1) + 'K', icon: 'ri-message-2-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Mensajes Totales', value: (privateMessagesDashboardStats.totalMessages / 1000000).toFixed(1) + 'M', icon: 'ri-chat-1-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Sin Leer', value: (privateMessagesDashboardStats.unreadMessages / 1000).toFixed(1) + 'K', icon: 'ri-mail-unread-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Contactos Activos', value: (privateMessagesDashboardStats.activeContacts / 1000).toFixed(1) + 'K', icon: 'ri-user-smile-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Promedio Diario', value: privateMessagesDashboardStats.dailyAvgMessages.toString(), icon: 'ri-bar-chart-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                {tab.badge && tab.badge > 0 && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'conversaciones' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {typeFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setTypeFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    typeFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {onlineContacts.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  </div>
                  <p className="text-xs font-bold text-foreground-900">{onlineContacts.length} contactos en línea</p>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {onlineContacts.map((contact) => (
                    <div key={contact.id} className="flex-shrink-0 flex flex-col items-center gap-1.5 p-2.5 rounded-xl bg-background-50 border border-background-200/70 hover:border-emerald-300/60 transition-all duration-200 cursor-pointer">
                      <div className="relative">
                        <img src={contact.photoUrl} alt={contact.name} className="w-10 h-10 rounded-full object-cover" />
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-background-50"></span>
                      </div>
                      <p className="text-[10px] font-medium text-foreground-700 line-clamp-1 w-16 text-center">{contact.name.split(' ')[0]}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pinnedConvs.length > 0 && (
              <div>
                <h4 className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <i className="ri-pushpin-fill text-rose-400 text-[10px]"></i>
                  Fijadas
                </h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {pinnedConvs.map((conv) => (
                    <ConversationPreview key={conv.id} conversation={conv} />
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-2">
                {typeFilter === 'todas' ? 'Todas las Conversaciones' : `Tipo: ${typeFilters.find((f) => f.id === typeFilter)?.label}`}
              </h4>
              {filteredConvs.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {filteredConvs.map((conv) => (
                    <ConversationPreview key={conv.id} conversation={conv} />
                  ))}
                </div>
              ) : (
                <p className="text-xs text-foreground-400 text-center py-8">No hay conversaciones de este tipo.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'adjuntos' && (
          <div>
            <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-4 text-center">4 Tipos de Adjuntos</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(attachmentTypeConfig).map(([key, config]) => (
                <div key={key} className="p-5 rounded-2xl bg-background-50 border border-background-200/70 text-center hover:border-rose-300/60 transition-all duration-200">
                  <div className={`w-12 h-12 rounded-full ${config.bgColor} flex items-center justify-center mx-auto mb-3`}>
                    <i className={`${config.icon} text-lg ${config.color}`}></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-950 mb-1">{config.label}</p>
                  <p className="text-[11px] text-foreground-500">
                    {key === 'imagen' && 'Compartí fotos, capturas y memes con tus contactos.'}
                    {key === 'audio' && 'Notas de voz para cuando escribir no alcanza.'}
                    {key === 'sticker' && 'Expresate con stickers únicos de RFW.'}
                    {key === 'gif' && 'Animaciones para darle vida a la conversación.'}
                  </p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              {[
                { label: 'Imágenes Compartidas', value: (privateMessagesDashboardStats.imagesShared / 1000).toFixed(1) + 'K', icon: 'ri-image-line', color: 'text-sky-600 bg-sky-50' },
                { label: 'Audios Enviados', value: (privateMessagesDashboardStats.audioSent / 1000).toFixed(1) + 'K', icon: 'ri-mic-line', color: 'text-rose-600 bg-rose-50' },
              ].map((stat, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70">
                  <div className={`w-10 h-10 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                    <i className={`${stat.icon} text-base`}></i>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-foreground-950">{stat.value}</p>
                    <p className="text-[10px] text-foreground-500">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'bloqueados' && (
          <div>
            <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-4 text-center">
              {blockedUsersList.length} Usuarios Bloqueados
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl mx-auto">
              {blockedUsersList.map((user) => (
                <div key={user.id} className="flex items-center gap-3 p-3.5 rounded-xl bg-background-50 border border-background-200/70">
                  <img src={user.photoUrl} alt={user.name} className="w-10 h-10 rounded-full object-cover opacity-50 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{user.name}</p>
                    <p className="text-[10px] text-foreground-500">{user.reason}</p>
                    <p className="text-[9px] text-foreground-400 mt-0.5">{user.blockedDate}</p>
                  </div>
                  <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                    <i className="ri-forbid-line text-xs text-red-500"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {privateMessagesRules.map((rule) => (
            <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50 text-center`}>
              <div className={`w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2`}>
                <i className={`${rule.icon} text-lg ${rule.color}`}></i>
              </div>
              <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
              <p className="text-[11px] text-foreground-500">{rule.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/mensajes-privados"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-message-2-line"></i>
            Abrir Mensajes Privados
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-broadcast-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Nunca interferir con la Cabina.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Los mensajes privados son para conversaciones personales. Saludos, peticiones de canciones y mensajes al aire van por la Cabina, no por MD. La magia de la radio está en compartir en vivo con toda la comunidad. Mantenemos los mensajes privados como un espacio de conexión personal complementario, nunca como reemplazo de la experiencia compartida que hace única a la radio.
          </p>
        </div>
      </div>
    </section>
  );
}